export type TemplateId = "paper" | "timeline" | "gallery" | "book" | "cinema" | "deck" | "focus" | "scrapbook" | "accordion" | "drift" | "circular" | "depth";
export type ExportQuality = "light" | "standard" | "archive";
export type AssetVariant = "original" | "preview" | "thumbnail";
export type MediaFit = "contain" | "cover";
export type FramePreset = "none" | "white" | "kraft" | "color" | "soft";
export type FrameSpacing = "compact" | "regular" | "wide";

export interface MediaPresentation {
  fit: MediaFit;
  frame: FramePreset;
  frameColor?: string;
  spacing: FrameSpacing;
}

export interface PhotoItem {
  id: string;
  assetId: string;
  title: string;
  caption: string;
  date?: string;
  location?: string;
  people: string[];
  order: number;
  presentation?: Partial<MediaPresentation>;
}

export interface PhotoFolioProject {
  schemaVersion: 1;
  id: string;
  title: string;
  subtitle: string;
  author: string;
  date: string;
  location: string;
  people: string[];
  coverAssetId?: string;
  items: PhotoItem[];
  template: TemplateId;
  exportQuality: ExportQuality;
  createdAt: string;
  updatedAt: string;
  lastOpenedAt: string;
  presentation?: MediaPresentation;
}

export interface AssetRecord {
  id: string;
  projectId: string;
  originalName: string;
  mimeType: string;
  width: number;
  height: number;
  size: number;
  capturedAt?: string;
  latitude?: number;
  longitude?: number;
  createdAt: string;
}

export interface AssetFileRecord {
  id: string;
  projectId: string;
  assetId: string;
  variant: AssetVariant;
  blob: Blob;
}

export interface AlbumPage {
  id: string;
  assetId: string;
  title: string;
  caption: string;
  date?: string;
  location?: string;
  presentation?: Partial<MediaPresentation>;
}

export interface AlbumDocument {
  title: string;
  subtitle: string;
  date: string;
  location: string;
  template: TemplateId;
  coverAssetId?: string;
  pages: AlbumPage[];
}

export const makeId = (prefix: string) =>
  `${prefix}-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;

export function createProject(title = "未命名影集"): PhotoFolioProject {
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    id: makeId("project"),
    title,
    subtitle: "把经过的光，留在这里。",
    author: "",
    date: "",
    location: "",
    people: [],
    items: [],
    template: "paper",
    exportQuality: "standard",
    presentation: { ...DEFAULT_MEDIA_PRESENTATION },
    createdAt: now,
    updatedAt: now,
    lastOpenedAt: now,
  };
}

export interface NormalizeProjectOptions {
  preserveTimestamps?: boolean;
}

const isTemplateId = (value: unknown): value is TemplateId =>
  value === "paper" || value === "timeline" || value === "gallery" || value === "book" || value === "cinema" || value === "deck" || value === "focus" || value === "scrapbook" || value === "accordion" || value === "drift" || value === "circular" || value === "depth";

const isExportQuality = (value: unknown): value is ExportQuality =>
  value === "light" || value === "standard" || value === "archive";

const isMediaFit = (value: unknown): value is MediaFit => value === "contain" || value === "cover";
const isFramePreset = (value: unknown): value is FramePreset => value === "none" || value === "white" || value === "kraft" || value === "color" || value === "soft";
const isFrameSpacing = (value: unknown): value is FrameSpacing => value === "compact" || value === "regular" || value === "wide";
export const isHexColor = (value: unknown): value is string => typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value);

export const DEFAULT_MEDIA_PRESENTATION: MediaPresentation = {
  fit: "contain",
  frame: "white",
  spacing: "regular",
};

export function normalizeMediaPresentation(input: unknown, fallback: MediaPresentation = DEFAULT_MEDIA_PRESENTATION): MediaPresentation {
  const candidate = input && typeof input === "object" ? input as Partial<MediaPresentation> : {};
  return {
    fit: isMediaFit(candidate.fit) ? candidate.fit : fallback.fit,
    frame: isFramePreset(candidate.frame) ? candidate.frame : fallback.frame,
    frameColor: isHexColor(candidate.frameColor) ? candidate.frameColor : fallback.frameColor,
    spacing: isFrameSpacing(candidate.spacing) ? candidate.spacing : fallback.spacing,
  };
}

export function resolveMediaPresentation(project: Pick<PhotoFolioProject, "presentation">, item?: Pick<PhotoItem, "presentation">): MediaPresentation {
  const projectPresentation = normalizeMediaPresentation(project.presentation);
  return normalizeMediaPresentation({ ...projectPresentation, ...(item?.presentation ?? {}) }, projectPresentation);
}

const safeTimestamp = (value: unknown, fallback: string) => {
  if (typeof value !== "string" || Number.isNaN(new Date(value).getTime())) return fallback;
  return value;
};

export function normalizeProject(input: unknown, options: NormalizeProjectOptions = {}): PhotoFolioProject {
  if (!input || typeof input !== "object") throw new Error("项目文件内容无效");
  const candidate = input as Partial<PhotoFolioProject>;
  if (candidate.schemaVersion !== 1 || typeof candidate.id !== "string" || !candidate.id || !Array.isArray(candidate.items)) {
    throw new Error("暂不支持此项目版本");
  }
  const now = new Date().toISOString();
  const defaults = createProject(typeof candidate.title === "string" && candidate.title ? candidate.title : "导入的影集");
  const indexedItems = candidate.items
    .map((rawItem, index) => ({ rawItem, index }))
    .filter(({ rawItem }) => Boolean(rawItem && typeof rawItem === "object" && typeof (rawItem as PhotoItem).assetId === "string" && (rawItem as PhotoItem).assetId));
  const items = indexedItems
    .sort((a, b) => {
      const aOrder = typeof (a.rawItem as PhotoItem).order === "number" && Number.isFinite((a.rawItem as PhotoItem).order)
        ? (a.rawItem as PhotoItem).order
        : Number.POSITIVE_INFINITY;
      const bOrder = typeof (b.rawItem as PhotoItem).order === "number" && Number.isFinite((b.rawItem as PhotoItem).order)
        ? (b.rawItem as PhotoItem).order
        : Number.POSITIVE_INFINITY;
      return aOrder - bOrder || a.index - b.index;
    })
    .map(({ rawItem }, index) => {
      const item = rawItem as Partial<PhotoItem>;
      return {
        id: typeof item.id === "string" && item.id ? item.id : makeId("item"),
        assetId: item.assetId as string,
        title: typeof item.title === "string" ? item.title : "",
        caption: typeof item.caption === "string" ? item.caption : "",
        date: typeof item.date === "string" && item.date ? item.date : undefined,
        location: typeof item.location === "string" && item.location ? item.location : undefined,
        people: Array.isArray(item.people) ? item.people.filter((person): person is string => typeof person === "string") : [],
        order: index,
        presentation: item.presentation && typeof item.presentation === "object" ? item.presentation : undefined,
      };
    });
  const people = Array.isArray(candidate.people)
    ? candidate.people.filter((person): person is string => typeof person === "string")
    : [];
  const coverAssetId = typeof candidate.coverAssetId === "string" && items.some((item) => item.assetId === candidate.coverAssetId)
    ? candidate.coverAssetId
    : items[0]?.assetId;
  const project: PhotoFolioProject = {
    ...defaults,
    id: candidate.id,
    title: typeof candidate.title === "string" ? candidate.title : defaults.title,
    subtitle: typeof candidate.subtitle === "string" ? candidate.subtitle : "",
    author: typeof candidate.author === "string" ? candidate.author : "",
    date: typeof candidate.date === "string" ? candidate.date : "",
    location: typeof candidate.location === "string" ? candidate.location : "",
    people,
    coverAssetId,
    items,
    template: isTemplateId(candidate.template) ? candidate.template : defaults.template,
    exportQuality: isExportQuality(candidate.exportQuality) ? candidate.exportQuality : defaults.exportQuality,
    createdAt: safeTimestamp(candidate.createdAt, defaults.createdAt),
    updatedAt: safeTimestamp(candidate.updatedAt, defaults.updatedAt),
    lastOpenedAt: safeTimestamp(candidate.lastOpenedAt, defaults.lastOpenedAt),
    presentation: normalizeMediaPresentation(candidate.presentation),
  };
  if (!options.preserveTimestamps) {
    project.updatedAt = now;
    project.lastOpenedAt = now;
  }
  return {
    ...project,
    schemaVersion: 1,
  };
}

export function buildAlbumDocument(project: PhotoFolioProject): AlbumDocument {
  return {
    title: project.title,
    subtitle: project.subtitle,
    date: project.date,
    location: project.location,
    template: project.template,
    coverAssetId: project.coverAssetId || project.items[0]?.assetId,
    pages: [...project.items]
      .sort((a, b) => a.order - b.order)
      .map((item) => ({
        id: item.id,
        assetId: item.assetId,
        title: item.title,
        caption: item.caption,
        date: item.date,
        location: item.location,
        presentation: resolveMediaPresentation(project, item),
      })),
  };
}

export function buildBookSpreads(pages: AlbumPage[]) {
  const spreads: AlbumPage[][] = [];
  for (let index = 0; index < pages.length; index += 2) {
    spreads.push(pages.slice(index, index + 2));
  }
  return spreads;
}
